<template>
  <transition name="bubble" v-on:afterEnter="here" @afterLeave="gone">
    <div class="bubbleHolder" :class="[direction, name]" v-if="show">
      <div ref="cont" class="bubbleTip" v-click-outside="close">
        <invite-tip v-if="name === 'invite'">
          <slot />
        </invite-tip>
        <div v-else class="content">
          <div class="message">
            <slot />
          </div>
          <div v-if="buttons" class="buttons btn-pair">
            <button v-for="(button, i) in buttons" @click="handleClick(button.event)" :key="'button-' + i">
              {{ button.text }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref } from 'vue';
import InviteTip from '@/components/InviteTip.vue';

defineProps({
  name: {},
  show: { default: false },
  direction: { default: 'above ' },
  buttons: { default: false },
});

const emit = defineEmits(['button', 'close']);

const visible = ref(false);

function handleClick(ev) {
  emit('button', ev);
  close();
}
function close() {
  if (visible.value) {
    emit('close');
  }
}
function here() {
  visible.value = true;
}
function gone() {
  visible.value = false;
}
</script>
<style lang="scss" scoped>
$bubble-tail-length: 18px;
$bubble-tail-width: 48px;
$bubble-tail-length-small: 6px;
$bubble-tail-width-small: 16px;

.bubbleHolder {
  position: absolute;
  height: 1px;
  width: 1px;
  z-index: 1;

  &.above {
    top: 0;
    left: 50%;
  }

  &.below {
    bottom: 0;
    left: 50%;
  }
}

.bubble-enter-active {
  transition: transform 0.3s cubic-bezier(0, 0.5, 0.5, 1);
}

.bubble-leave-active {
  transition: transform 0.3s cubic-bezier(0.5, 0, 1, 0.5);
}

.bubble-enter-from,
.bubble-leave-to {
  transform: scale(0);
}

:deep(.bubbleTip) {
  background: var(--c__bg);
  filter: drop-shadow(0px 1px 10px var(--c__shadow));
  position: absolute;
  padding: rem(28px);
  width: rem($sidebar-width);
  margin-left: rem($sidebar-width * -0.5);
  transition: transform 10.5s ease;

  will-change: filter;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden; /* Chrome and Safari */
  -moz-backface-visibility: hidden; /* Firefox */

  h2 {
    font-weight: 800;
    line-height: 1;
  }

  &::after {
    content: '';
    height: 0;
    width: 0;
    position: absolute;
    left: 50%;
    margin-left: rem(math.div($bubble-tail-width, -2));
  }

  .below & {
    top: rem($bubble-tail-length);
    transform-origin: 50% rem(0 - $bubble-tail-length) !important;
    text-align: center;
    &::after {
      border-top: rem($bubble-tail-length) solid transparent;
      border-bottom: rem($bubble-tail-length) solid var(--c__bg);
      border-left: rem(math.div($bubble-tail-width, 2)) solid transparent;
      border-right: rem(math.div($bubble-tail-width, 2)) solid transparent;
      top: rem(0 - ($bubble-tail-length * 2));
      margin-top: 1px;
    }
  }

  .above & {
    bottom: rem($bubble-tail-length);
    transform-origin: 50% rem(0 - $bubble-tail-length) !important;
    text-align: center;
    &::after {
      border-bottom: rem($bubble-tail-length) solid transparent;
      border-top: rem($bubble-tail-length) solid var(--c__bg);
      border-left: rem(math.div($bubble-tail-width, 2)) solid transparent;
      border-right: rem(math.div($bubble-tail-width, 2)) solid transparent;
      bottom: rem(0 - ($bubble-tail-length * 2));
      margin-bottom: 1px;
    }
  }

  .rightTail & {
    margin-left: rem(($sidebar-width * -0.5) - 57px);
    &::after {
      margin-left: rem(math.div($bubble-tail-width, -2) + 57px);
    }
  }

  .small.leftTail & {
    padding: rem(10px);
    width: auto;
    margin-left: rem(math.div($bubble-tail-width-small, -2));
    bottom: rem($bubble-tail-length-small);
    transform-origin: 50% rem(0 - $bubble-tail-length-small) !important;
    &::after {
      left: 0;
      margin-left: 0;
      border-bottom: rem($bubble-tail-length-small) solid transparent;
      border-top: rem($bubble-tail-length-small) solid var(--c__bg);
      border-left: rem(math.div($bubble-tail-width-small, 2)) solid transparent;
      border-right: rem(math.div($bubble-tail-width-small, 2)) solid transparent;
      bottom: rem(0 - ($bubble-tail-length-small * 2));
    }
  }

  .buttons {
    margin-top: 1em;
    height: rem(50px);
    border-width: 2px;
    --c__button: var(--c__text-loud);

    button {
      display: flex;
      justify-content: center;
      align-items: center;
      @include triggered {
        color: var(--c__bg);
        .text {
          text-decoration: none;
        }
      }
    }
    .text {
      margin: 0;
    }
  }
}

.warn .message,
.reviewBubble .message {
  @include type-size('medium');
}

.warn .buttons,
.exit .warn .buttons {
  --c__button: var(--c__warn);
}
</style>
